import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { lockScroll, unlockScroll } from 'utils/scrolling'
import { useMediaQuery } from 'hooks'

import Close from 'images/close.svg'
import { isAnalyticsAllowed, isAnalyticsConsentManaged, subscribeToAnalyticsConsent, withAnalyticsConsent } from './consent'
import { ANALYTICS_CONSENT_CHANGED } from './messenger'

const CloseButton = ({ onClick }) => (
  <div
    id="sc-close-modal"
    data-testid="close-modal"
    style={styles.close}
    onClick={onClick}
  >
    <Close />
  </div>
)

function Overlay({ onClick, children }) {
  return (
    <div style={styles.overlay} onClick={onClick} id='sidechef-widget-modal'>
      {children}
    </div>
  )
}

const MODEL_DEFAULT_MAX_WIDTH = 768

export default function Modal({ visible = false, onClose, iframeSrc, modalMaxWidth = MODEL_DEFAULT_MAX_WIDTH }) {
  const inDesktop = useMediaQuery('screen and (min-width:1220px)')
  const iframeContainerStyle = inDesktop ? styles.modal : styles.modalMobile
  const iframeRef = useRef(null)

  const sendAnalyticsConsent = () => {
    if (!isAnalyticsConsentManaged() || !iframeRef.current?.contentWindow) return
    iframeRef.current.contentWindow.postMessage(
      { type: ANALYTICS_CONSENT_CHANGED, analyticsAllowed: isAnalyticsAllowed() },
      new URL(iframeSrc, window.location.href).origin
    )
  }

  useEffect(() => {
    if (!visible) return undefined
    return subscribeToAnalyticsConsent(sendAnalyticsConsent)
  }, [visible, iframeSrc])

  useEffect(() => {
    if (visible) {
      lockScroll()
    } else {
      unlockScroll()
    }
  }, [visible])

  if (!visible) return null

  return createPortal(
    <Overlay onClick={onClose}>
      <div
        data-testid="modal"
        style={{ ...iframeContainerStyle, maxWidth: inDesktop ? modalMaxWidth : 'unset' }}
        onClick={e => e.stopPropagation()}
      >
        <iframe
          ref={iframeRef}
          data-testid="popup-iframe"
          src={withAnalyticsConsent(iframeSrc)}
          style={styles.iframe}
          onLoad={sendAnalyticsConsent}
        />
        <CloseButton onClick={onClose} />
      </div>
    </Overlay>,
    document.body
  )
}

const styles = {
  overlay: {
    fontFamily: 'sans-serif',
    position: 'fixed',
    zIndex: 'calc(infinity)',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  modal: {
    position: 'relative',
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '80%',
    maxWidth: '768px',
    height: 'calc(100vh - 96px)',
    minHeight: '500px',
    overflow: 'hidden',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '48px',
  },
  modalMobile: {
    position: 'relative',
    backgroundColor: '#fff',
    borderRadius: '0',
    width: '100%',
    height: '100%',
  },
  iframe: {
    border: 'none',
    width: '100%',
    height: '100%',
    display: 'block'
  },
  close: {
    cursor: 'pointer',
    position: 'absolute',
    top: 16,
    right: 16
  },
}
